import { keepPreviousData, QueryClient } from '@tanstack/query-core'
import { InfiniteData, useQuery, useQueryClient } from '@tanstack/react-query'
import axios, { AxiosRequestConfig } from 'axios'
import { produce } from 'immer'
import queryString from 'query-string'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { BulkTransactionUpdateSuccessToastMessage } from 'src/app/Toast/Toast'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import { PaginationResponse } from 'src/models/pagination.models'
import { PeopleContactsResponse } from 'src/models/people.models'
import {
  TransactionDetailModel,
  TransactionDetailResponse,
  TransactionListModel,
  TransactionListResponse,
} from 'src/models/transaction.models'
import { UserListModel, UserListResponse } from 'src/models/user.models'
import { closeModal } from 'src/navigation/Lnk'
import {
  ExpenseCategoryLabelFiltersBody,
  ExpenseCategoryLabelResponse,
} from 'src/queries/organizations/expenseCategories.queries'
import {
  ORG_EXPENSE_REPORTS_QK,
  TRANSACTIONS_COUNT_QK,
  TRANSACTIONS_QK,
  transactionsQueryKeys,
  USERS_QK,
} from 'src/queries/queries.constants'
import {
  ExtendBaseInfiniteQueryOptions,
  ExtendBaseQueryOptions,
  getListQueryKeys,
  getNextExtendPageParam,
  getNextTransactionPageParam,
  MutationMethod,
  useExtendDelete,
  useExtendInfiniteQuery,
  useExtendMutation,
  useExtendQuery,
  useExtendSuspenseQuery,
  useRequestConfig,
} from 'src/queries/queries.utils'
import { getAsyncTransactionUpdateJobStatus } from 'src/redux/actions/data/transactions.actions'
import { selectUserIdFromState } from 'src/redux/selectors/auth.selectors'
import { PatchTransactionRequest } from 'src/requests/patchTransaction.request'
import {
  PatchBulkTransactionExpenseDataRequest,
  PatchTransactionExpenseDataRequest,
} from 'src/requests/patchTransactionExpenseData.request'
import { PostSingleTransactionExpenseCategoryRequest } from 'src/requests/postSingleTransactionExpenseCategory.request'
import { UpdateTransactionExpenseDetailsRequest } from 'src/requests/updateTransactionExpenseDetails.request'

export interface TransactionsResponse {
  report: {
    page: number
    perPage: number
    count: number
    transactions: TransactionListResponse[]
  }
}

// Note that these models are built uniquely using the more general PaginationModel, but don't extend it
// because we currently have tech debt where certain responses don't return fully accurate pagination data
// i.e. we can't get totalItems or numberOfPages for Transactions Reports
export interface TransactionsDataModel {
  transactions: TransactionListModel[] | undefined
  page: number
}

export const selectTransactionListModel = (
  response: InfiniteData<TransactionsResponse, number>,
): TransactionsDataModel => ({
  transactions: response?.pages
    .flatMap((page) =>
      page.report.transactions.map((txn) => new TransactionListModel(txn)),
    )
    // only take first instance of each transaction
    // deal with edge cases that arise from pusher events loading in data realtime
    // TODO: React Query this approach will not handle cases when transactions are deleted locally from the list
    .filter(
      (txn1, i, txns) => txns.findIndex((txn2) => txn2.id === txn1.id) === i,
    ),
  page: response.pageParams.length - 1,
})

interface UseQueryTransactionsOptions
  extends ExtendBaseInfiniteQueryOptions<
    TransactionsResponse,
    number,
    TransactionsDataModel
  > {
  filters: TransactionsFiltersBody | undefined
  myTransactions?: boolean | undefined
  locationKey?: string
}

export const useQueryTransactions = ({
  filters,
  myTransactions = false,
  enabled = true,
  perPage = 50,
  locationKey,
  ...rest
}: UseQueryTransactionsOptions) => {
  const currentUserId = useSelector(selectUserIdFromState)
  return useExtendInfiniteQuery({
    // note that the queryKey contains a boolean for abstracting away the separate endpoints for your and all transactions
    queryKey: transactionsQueryKeys.list(filters, locationKey),
    queryUrl: ({ pageParam }) => {
      const requestUrl = myTransactions
        ? `/reports/transactions/v2/${currentUserId}`
        : '/reports/transactions/v2'
      return `${requestUrl}?${queryString.stringify({ ...filters, page: pageParam, perPage })}`
    },
    enabled:
      enabled &&
      filters !== undefined &&
      (myTransactions ? currentUserId !== undefined : true),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _, __) =>
      getNextTransactionPageParam(
        lastPage.report.page,
        lastPage.report.perPage,
        lastPage.report.count,
      ),
    select: selectTransactionListModel,
    // re-fetches happen in place without resetting back to an empty state, less flashing of content
    placeholderData: keepPreviousData,
    ...rest,
  })
}

// TODO: as part of the filters refactor, we should be able to have a FiltersContext that can be used to
// call useQueryTransactions directly without needing this function
export const useQueryFetchedTransactions = () => {
  const queryClient = useQueryClient()
  const currentQueryKeys = getListQueryKeys(
    queryClient,
    [TRANSACTIONS_QK],
    [TRANSACTIONS_COUNT_QK],
  )

  if (currentQueryKeys.length > 1) {
    // TODO: React Query will we need to support multiple transaction list queries with different filters at once?
    /* eslint-disable-next-line no-console */
    console.warn(
      'useQueryFetchedTransactions: multiple transactions queries detected, using the first one',
    )
  }
  const queryKey = currentQueryKeys[0] ?? []

  // TODO: this is a great candidate for implementing a runtime validation library like zod
  // casting because if the order of the query keys changes, this will throw a type error
  const [_, filters] = queryKey
  const cacheKeyComponents = transactionsQueryKeys.list(
    filters as TransactionsFiltersBody | undefined,
  )

  return useQueryTransactions({
    filters: cacheKeyComponents.at(-1) as TransactionsFiltersBody | undefined,
    refetchOnMount: false,
  })
}

export const updateTransactionListResponses = (
  queryClient: QueryClient,
  options: {
    transactionIds: string[]
    updater?: (transaction: TransactionListResponse) => TransactionListResponse
    newTransactions?: TransactionListResponse[]
  },
  updateMetrics: boolean = true, // only set to false where multiple txns are being updating individually to avoid exessive refetching (currently only usePatchBulkTransactionExpenseData)
) => {
  const { transactionIds, updater, newTransactions } = options
  const currentQueryKeys = getListQueryKeys(
    queryClient,
    [TRANSACTIONS_QK],
    [TRANSACTIONS_COUNT_QK],
  )

  currentQueryKeys.forEach((queryKey) => {
    queryClient.setQueryData(
      queryKey,
      (
        oldTransactions: InfiniteData<TransactionsResponse, number> | undefined,
      ) => {
        // runtime validation of type for any potential edge cases
        if (!oldTransactions?.pages?.[0]?.report?.transactions)
          return oldTransactions

        const updatedPages: TransactionsResponse[] = oldTransactions.pages.map(
          (page) => {
            return produce(page, (draft) => {
              draft.report.transactions = page.report.transactions.map(
                (txn) => {
                  if (transactionIds?.includes(txn.id)) {
                    // if there's a new transaction, use that
                    return (
                      newTransactions?.find((newTxn) => newTxn.id === txn.id) ??
                      updater?.(txn) ??
                      txn
                    )
                  } else {
                    return txn
                  }
                },
              )
            })
          },
        )

        // If new transactions are provided, we may need to add them to the first page
        if (newTransactions !== undefined) {
          const oldTransactionIds = new Set(
            oldTransactions?.pages.flatMap((page) =>
              page.report.transactions.map((txn) => txn.id),
            ),
          )

          // Filter new transactions that are not already in the old transactions list
          const newTransactionsToAdd = newTransactions.filter(
            (txn) => !oldTransactionIds.has(txn.id),
          )

          if (newTransactionsToAdd.length > 0) {
            // Add the new transactions to the top of the first page
            updatedPages[0] = produce(updatedPages[0], (draft) => {
              draft.report.transactions = [
                ...newTransactionsToAdd,
                ...updatedPages[0].report.transactions,
              ]
            })
          }
        }

        return produce(oldTransactions, (draft) => {
          draft.pages = updatedPages
        })
      },
    )
  })
  if (updateMetrics) {
    // invalidate metrics - we dont know what report the transaction was in, so invalidate all report metrics
    queryClient.invalidateQueries({ queryKey: [ORG_EXPENSE_REPORTS_QK] })
  }
}

export const updateTransactionDetailResponse = (
  queryClient: QueryClient,
  options: {
    transactionId: string
    updater?: (
      transaction: TransactionDetailResponse,
    ) => TransactionDetailResponse
    newTransaction?: TransactionDetailResponse
  },
  updateMetrics?: boolean,
) => {
  const { transactionId, updater, newTransaction } = options

  const updaterSafe = (old: TransactionDetailResponse | undefined) => {
    if (newTransaction) return newTransaction
    if (!old) return undefined
    return updater?.(old) ?? undefined
  }
  // update detail response in cache
  queryClient.setQueryData(
    transactionsQueryKeys.detail(transactionId),
    updaterSafe,
  )
  // update list response in cache

  const listUpdater = updater
    ? (txn: TransactionListResponse) =>
        // pass in stub data to the updater since it's going to throw it out anyway
        updater({ ...txn, receiptAttachments: [], virtualCardId: undefined })
    : undefined

  updateTransactionListResponses(
    queryClient,
    {
      transactionIds: [transactionId],
      updater: listUpdater,
      newTransactions: newTransaction ? [newTransaction] : undefined,
    },
    updateMetrics,
  )
}

// detail screen queries and mutations
interface UseQueryTransactionDetailOptions
  extends ExtendBaseQueryOptions<
    TransactionDetailResponse,
    TransactionDetailModel
  > {
  transactionId: string | undefined
}
export const useQueryTransactionDetail = ({
  transactionId,
  enabled = true,
  ...rest
}: UseQueryTransactionDetailOptions) => {
  const queryClient = useQueryClient()

  return useExtendQuery({
    queryKey: transactionsQueryKeys.detail(transactionId),
    queryUrl: () => `/transactions/${transactionId}`,
    onSuccess: (data) => {
      updateTransactionListResponses(queryClient, {
        transactionIds: [data.id],
        updater: () => data,
      })
    },
    enabled: enabled && !!transactionId,
    select: (data) => new TransactionDetailModel(data),
    ...rest,
  })
}

export const queryTransactionDetail = async (
  queryClient: QueryClient,
  config: AxiosRequestConfig,
  transactionId: string,
  updateMetrics?: boolean,
) => {
  try {
    const transactionResponse = (
      await queryClient.fetchQuery({
        queryKey: transactionsQueryKeys.detail(transactionId),
        // TODO: do we really need to pass in the config here?
        queryFn: () =>
          axios.get<TransactionDetailResponse>(
            `/transactions/${transactionId}`,
            config,
          ),
        retry: false,
      })
    )?.data

    // manually add the transaction detail response
    // the above fetchQuery call will automatically add the transaction to the cache for
    // but this call also adds the transaction to the cache for the list query
    updateTransactionDetailResponse(
      queryClient,
      {
        transactionId: transactionResponse.id,
        newTransaction: transactionResponse,
      },
      updateMetrics,
    )
  } catch (e) {
    // TODO: implement better error handling/expose error to front-end
    /* eslint-disable-next-line no-console */
    console.warn(`Could not get ${transactionId} detail`, e)
  }
}

export const usePatchTransaction = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    TransactionDetailResponse,
    { transactionId: string; body: Partial<PatchTransactionRequest> }
  >(MutationMethod.PATCH, {
    mutationUrl: ({ transactionId }) => `/transactions/${transactionId}`,
    onSuccess: (data, variables) => {
      updateTransactionDetailResponse(queryClient, {
        transactionId: variables.transactionId,
        updater: () => data,
      })
    },
  })
}

export const usePatchTransactionExpenseData = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    TransactionDetailResponse,
    { transactionId: string; body: Partial<PatchTransactionExpenseDataRequest> }
  >(MutationMethod.PATCH, {
    mutationUrl: ({ transactionId }) =>
      `/transactions/${transactionId}/expensecategories`,
    onSuccess: (data, variables) => {
      updateTransactionDetailResponse(queryClient, {
        transactionId: variables.transactionId,
        updater: (old) => ({
          ...old,
          expenseDetails: data.expenseDetails,
        }),
      })
    },
  })
}

export const useUpdateTransactionExpenseData = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    TransactionDetailResponse,
    {
      transactionId: string
      body: UpdateTransactionExpenseDetailsRequest
    }
  >(MutationMethod.PATCH, {
    mutationUrl: ({ transactionId }) =>
      `/transactions/${transactionId}/expensedata`,
    onSuccess: (data, variables) => {
      updateTransactionDetailResponse(queryClient, {
        transactionId: variables.transactionId,
        newTransaction: data,
      })
    },
  })
}

export const useSplitTransactionExpenseData = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    TransactionDetailResponse,
    {
      transactionId: string
      body: UpdateTransactionExpenseDetailsRequest
    }
  >(MutationMethod.PUT, {
    mutationUrl: ({ transactionId }) =>
      `/transactions/${transactionId}/expensedata/split`,
    onSuccess: (data, variables) => {
      updateTransactionDetailResponse(queryClient, {
        transactionId: variables.transactionId,
        newTransaction: data,
      })
    },
  })
}

export const useUnsplitTransactionExpenseData = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    TransactionDetailResponse,
    {
      transactionId: string
      body: UpdateTransactionExpenseDetailsRequest
    }
  >(MutationMethod.PATCH, {
    mutationUrl: ({ transactionId }) =>
      `/transactions/${transactionId}/expensedata/unsplit`,
    onSuccess: (data, variables) => {
      updateTransactionDetailResponse(queryClient, {
        transactionId: variables.transactionId,
        newTransaction: data,
      })
    },
  })
}

export const usePatchBulkTransactionExpenseData = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const requestConfig = useRequestConfig()

  return useExtendMutation<
    { jobId?: string },
    {
      filters?: TransactionsFiltersBody
      body: Partial<PatchBulkTransactionExpenseDataRequest>
    }
  >(MutationMethod.PATCH, {
    mutationUrl: (variables) =>
      `/transactions/expensedata/bulkupdate?${queryString.stringify({ ...variables?.filters })}`,
    onSuccess: (data, variables) => {
      dispatch(closeModal())
      // TODO - can remove sync flow once bulk_select_fast_follows flag is removed
      if (data.jobId !== undefined && data.jobId !== null) {
        // async update
        dispatch(getAsyncTransactionUpdateJobStatus(data.jobId))
      } else {
        // sync update
        variables.body.transactionIds?.forEach((transactionId) => {
          // TODO: React Query move to updateTransactionDetailResponse when backend returns the correct data
          queryTransactionDetail(
            queryClient,
            requestConfig,
            transactionId,
            false,
          )
        })
        // invalidate metrics - we dont know what report the transaction was in, so invalidate all report metrics
        queryClient.invalidateQueries({ queryKey: [ORG_EXPENSE_REPORTS_QK] }) // TODO: remove once above TODO is resolved
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: (
              <BulkTransactionUpdateSuccessToastMessage
                count={variables.body.transactionIds?.length || 0}
              />
            ),
          }),
        )
      }
    },
  })
}

export const useQueryTransactionsPerson = (personId: string | undefined) => {
  const requestConfig = useRequestConfig()

  // leaving this as an example of how we can implement custom queryFn logic if the useExtendQuery doesn't fit the use case
  return useQuery({
    queryKey: [USERS_QK, personId],
    queryFn: async () => {
      return await axios
        .get<PeopleContactsResponse>(
          `/connections?userToId=${personId}`,
          requestConfig,
        )
        .then((response) => {
          if (response.data.userConnections.length > 0) {
            return new UserListModel(response.data.userConnections[0].userTo)
          } else {
            return axios
              .get<UserListResponse>(`/users/${personId}`, requestConfig)
              .then((response) => new UserListModel(response.data))
          }
        })
    },
    enabled: personId !== undefined,
  })
}

// Delete transactions from the list responses for ALL list views
export const deleteTransactionListResponses = (
  queryClient: QueryClient,
  newTransactionIds: string[],
) => {
  const currentQueryKeys = getListQueryKeys(
    queryClient,
    [TRANSACTIONS_QK],
    [TRANSACTIONS_COUNT_QK],
  )
  const lookup = new Set(newTransactionIds)
  currentQueryKeys.forEach((queryKey) => {
    queryClient.setQueryData(
      queryKey,
      (
        oldTransactions: InfiniteData<TransactionsResponse, number> | undefined,
      ) => {
        // runtime validation of type for any potential edge cases
        if (!oldTransactions?.pages?.[0]?.report) return oldTransactions
        return {
          ...oldTransactions,
          pages: oldTransactions.pages.map((page) => ({
            ...page,
            report: {
              ...page.report,
              transactions: page.report.transactions.filter(
                (txn) => !lookup.has(txn.id),
              ),
            },
          })),
        }
      },
    )
  })
  // invalidate metrics - we dont know what report the transaction was in, so invalidate all report metrics
  queryClient.invalidateQueries({ queryKey: [ORG_EXPENSE_REPORTS_QK] })
}

export const deleteTransactionDetailResponse = (
  queryClient: QueryClient,
  transactionId: string,
) => {
  // delete detail response in cache
  queryClient.setQueryData(
    transactionsQueryKeys.detail(transactionId),
    undefined,
  )
  // delete list response in cache
  deleteTransactionListResponses(queryClient, [transactionId])
}

export const useDeletePhysicalTransaction = () => {
  const queryClient = useQueryClient()
  return useExtendDelete<TransactionDetailResponse, { transactionId: string }>({
    mutationUrl: ({ transactionId }) => `/transactions/${transactionId}`,
    onSuccess: (_, { transactionId }) => {
      deleteTransactionDetailResponse(queryClient, transactionId)
    },
  })
}

interface UseQueryTransactionsCountOptions
  extends ExtendBaseQueryOptions<{ count: number }, number> {
  filters: TransactionsFiltersBody | undefined
  locationKey?: string
}

export const useSuspenseQueryTransactionsCount = ({
  filters,
  ...rest
}: UseQueryTransactionsCountOptions) => {
  return useExtendSuspenseQuery({
    queryKey: transactionsQueryKeys.count(filters),
    queryUrl: () =>
      `/reports/transactions/count?${queryString.stringify({ ...filters })}`,
    select: (data) => data.count,
    ...rest,
  })
}

export const useQueryTransactionsCount = ({
  filters,
  enabled,
  locationKey,
  ...rest
}: UseQueryTransactionsCountOptions) => {
  return useExtendQuery({
    queryKey: transactionsQueryKeys.count(filters, locationKey),
    queryUrl: () =>
      `/reports/transactions/count?${queryString.stringify({ ...filters })}`,
    select: (data) => data.count,
    enabled: enabled && filters !== undefined,
    ...rest,
  })
}

export interface PostSingleTransactionExpenseCategoryVariables {
  transactionId: string
  categoryId: string
  body: PostSingleTransactionExpenseCategoryRequest
}

export const usePostSingleTransactionExpenseCategory = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    TransactionDetailResponse,
    PostSingleTransactionExpenseCategoryVariables
  >(MutationMethod.POST, {
    mutationKey: transactionsQueryKeys.postSingleExpenseCategory(),
    mutationUrl: ({ transactionId, categoryId }) =>
      `/transactions/${transactionId}/expensedata/categories/${categoryId}`,
    onSuccess: (data, variables) => {
      updateTransactionDetailResponse(queryClient, {
        transactionId: variables.transactionId,
        updater: (old) => ({
          ...old,
          expenseDetails: data.expenseDetails,
          managerReviewStatus: data.managerReviewStatus,
        }),
      })
    },
  })
}

interface TransactionExpenseCategoryLabelDetails {
  rank: number
  similarityScore: number
  similarTransactionId?: string
}

interface TransactionExpenseCategoryLabelResponse
  extends ExpenseCategoryLabelResponse {
  suggestionDetails?: TransactionExpenseCategoryLabelDetails
}

interface TransactionExpenseCategoryLabelsResponse {
  pagination: PaginationResponse
  expenseLabels: TransactionExpenseCategoryLabelResponse[]
}

interface QueryTransactionExpenseCategoryLabelsOptions
  extends ExtendBaseInfiniteQueryOptions<
    TransactionExpenseCategoryLabelsResponse,
    number,
    TransactionExpenseCategoryLabelResponse[]
  > {
  expenseCategoryId: string | undefined
  transactionId: string | undefined
  filters: ExpenseCategoryLabelFiltersBody
}

export const useQueryTransactionExpenseCategoryLabels = ({
  expenseCategoryId,
  transactionId,
  filters,
  enabled = true,
  ...rest
}: QueryTransactionExpenseCategoryLabelsOptions) => {
  return useExtendInfiniteQuery({
    queryKey: transactionsQueryKeys.expenseCategoryLabels(
      transactionId,
      expenseCategoryId,
      filters,
    ),
    queryUrl: ({ pageParam }) =>
      `transactions/${transactionId}/expensedata/categories/${expenseCategoryId}/labels?${queryString.stringify({ page: pageParam, ...filters }, { skipEmptyString: true })}`,
    enabled:
      enabled && expenseCategoryId !== undefined && transactionId !== undefined,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => getNextExtendPageParam(lastPage.pagination),
    select: (data) => data.pages.flatMap((page) => page.expenseLabels),
    gcTime: 30000,
    staleTime: 30000,
    ...rest,
  })
}
